import React from 'react'
import { Link } from 'react-router-dom'
import { Shield, Scan, TrendingUp, MapPin, BarChart3, Lock, ArrowRight, CheckCircle, Zap, Globe } from 'lucide-react'

const LandingPage = () => {
  const features = [
    { icon: Scan, title: 'NFC Authentication', desc: 'Instant product verification using NFC technology with 87.2% AI accuracy.' },
    { icon: TrendingUp, title: 'Real-time Analytics', desc: 'Advanced data science insights with trend analysis and anomaly detection.' },
    { icon: MapPin, title: 'Geographic Intelligence', desc: 'Interactive heat maps showing counterfeit detection hotspots across South Africa.' },
    { icon: BarChart3, title: 'Brand Protection', desc: 'Manufacturer-scoped dashboards with brand vulnerability metrics.' },
    { icon: Lock, title: 'Secure Platform', desc: 'JWT-based authentication with role-based access control.' },
    { icon: Globe, title: 'Industry Coverage', desc: 'Complete coverage of the South African liquor market with 5 major manufacturers.' },
  ]

  const stats = [
    { value: '3,700+', label: 'Scans Processed' },
    { value: '87.2%', label: 'Detection Accuracy' },
    { value: '15', label: 'Cities Covered' },
    { value: '20+', label: 'Products Protected' },
  ]

  return (
    <div className="min-h-screen bg-white">
      <nav className="fixed top-0 left-0 right-0 z-50 px-4 pt-4">
        <div className="max-w-7xl mx-auto bg-white/95 backdrop-blur-md rounded-2xl shadow-lg border border-gray-100/50 px-6 py-3 flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <Shield className="h-7 w-7 text-[#1E4D40]" />
            <span className="text-lg font-bold text-gray-900">Verifi<span className="text-[#1E4D40]">AI</span></span>
          </div>
          <div className="hidden md:flex items-center space-x-8">
            <a href="#features" className="text-sm text-gray-600 hover:text-gray-900">Features</a>
            <a href="#stats" className="text-sm text-gray-600 hover:text-gray-900">Stats</a>
            <a href="#screenshots" className="text-sm text-gray-600 hover:text-gray-900">Product</a>
          </div>
          <div className="flex items-center space-x-3">
            <Link to="/login" className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-900">Sign in</Link>
            <Link to="/register" className="px-5 py-2.5 text-sm font-medium text-white bg-[#1E4D40] rounded-xl hover:bg-[#163a30] shadow-sm">
              Get Started <ArrowRight className="inline h-4 w-4 ml-1" />
            </Link>
          </div>
        </div>
      </nav>

      <section className="pt-32 pb-20 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center px-3 py-1 bg-green-50 rounded-full text-xs font-medium text-green-700 mb-6">
                <Zap className="h-3 w-3 mr-1" /> AI-Powered Counterfeit Detection
              </div>
              <h1 className="text-5xl lg:text-6xl font-bold text-gray-900 leading-tight mb-6">
                Protecting Quality,<br />
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#1E4D40] to-[#A3E635]">Ensuring Authenticity</span>
              </h1>
              <p className="text-lg text-gray-600 mb-8 leading-relaxed">
                Advanced AI-powered counterfeit detection for the South African liquor market.
                Join thousands of businesses protecting their brands and customers with real-time NFC verification.
              </p>
              <div className="flex flex-col sm:flex-row items-start sm:items-center space-y-3 sm:space-y-0 sm:space-x-4">
                <Link to="/register" className="w-full sm:w-auto px-8 py-4 text-base font-medium text-white bg-[#1E4D40] rounded-2xl hover:bg-[#163a30] shadow-lg hover:shadow-xl transition-all text-center">
                  Start Free Trial <ArrowRight className="inline h-5 w-5 ml-2" />
                </Link>
                <Link to="/login" className="w-full sm:w-auto px-8 py-4 text-base font-medium text-gray-700 bg-white border border-gray-200 rounded-2xl hover:bg-gray-50 shadow-sm text-center">
                  Sign In
                </Link>
              </div>
              <div className="mt-8 flex items-center space-x-6 text-sm text-gray-500">
                <span className="flex items-center"><CheckCircle className="h-4 w-4 text-green-500 mr-1" /> Free 14-day trial</span>
                <span className="flex items-center"><CheckCircle className="h-4 w-4 text-green-500 mr-1" /> No credit card</span>
                <span className="flex items-center"><CheckCircle className="h-4 w-4 text-green-500 mr-1" /> Cancel anytime</span>
              </div>
            </div>
            <div className="relative">
              <div className="bg-gradient-to-br from-[#1E4D40] to-[#2d6b5a] rounded-3xl p-8 shadow-2xl">
                <div className="bg-white/10 backdrop-blur rounded-2xl p-6 space-y-4">
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 bg-[#A3E635] rounded-xl flex items-center justify-center">
                      <Shield className="h-5 w-5 text-[#1E4D40]" />
                    </div>
                    <div>
                      <p className="text-white font-medium">Product Verified</p>
                      <p className="text-white/60 text-sm">Amarula Cream - Authentic</p>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="bg-white/10 rounded-xl p-3">
                      <p className="text-white/60 text-xs">Confidence</p>
                      <p className="text-white font-bold text-lg">98.7%</p>
                    </div>
                    <div className="bg-white/10 rounded-xl p-3">
                      <p className="text-white/60 text-xs">Scan Time</p>
                      <p className="text-white font-bold text-lg">0.3s</p>
                    </div>
                  </div>
                  <div className="bg-[#A3E635]/20 rounded-xl p-3 flex items-center space-x-2">
                    <CheckCircle className="h-5 w-5 text-[#A3E635]" />
                    <span className="text-white text-sm">NFC Tag Validated Successfully</span>
                  </div>
                </div>
              </div>
              <div className="absolute -z-10 top-4 left-4 right-4 bottom-4 bg-[#A3E635]/20 rounded-3xl" />
            </div>
          </div>
        </div>
      </section>

      <section id="stats" className="py-16 bg-[#F3F4F6]">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, i) => (
              <div key={i} className="text-center">
                <p className="text-4xl font-bold text-[#1E4D40]">{stat.value}</p>
                <p className="text-sm text-gray-500 mt-1">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section id="features" className="py-20 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Everything you need to fight counterfeits</h2>
            <p className="text-lg text-gray-500 max-w-2xl mx-auto">Comprehensive tools for product authentication, analytics, and brand protection.</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, i) => {
              const Icon = feature.icon
              return (
                <div key={i} className="bg-white rounded-3xl p-8 shadow-sm hover:shadow-md transition-shadow border border-gray-100">
                  <div className="w-12 h-12 bg-gradient-to-br from-[#1E4D40] to-[#2d6b5a] rounded-2xl flex items-center justify-center mb-4">
                    <Icon className="h-6 w-6 text-white" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">{feature.title}</h3>
                  <p className="text-gray-500 text-sm leading-relaxed">{feature.desc}</p>
                </div>
              )
            })}
          </div>
        </div>
      </section>

      <section id="screenshots" className="py-20 bg-[#F3F4F6] px-4">
        <div className="max-w-7xl mx-auto text-center">
          <h2 className="text-3xl font-bold text-gray-900 mb-4">Built for the SA Liquor Industry</h2>
          <p className="text-lg text-gray-500 mb-12 max-w-2xl mx-auto">Monitoring brands from Distell, SAB, KWV, Heineken, and Pernod Ricard across 15 South African cities.</p>
          <div className="grid md:grid-cols-3 gap-6">
            <div className="bg-white rounded-3xl p-6 shadow-sm">
              <div className="h-40 bg-gradient-to-br from-[#A3E635]/20 to-[#1E4D40]/10 rounded-2xl flex items-center justify-center mb-4">
                <BarChart3 className="h-16 w-16 text-[#1E4D40]/40" />
              </div>
              <h3 className="font-semibold text-gray-900">Real-time Dashboard</h3>
              <p className="text-sm text-gray-500 mt-1">Live scan statistics with trend analysis</p>
            </div>
            <div className="bg-white rounded-3xl p-6 shadow-sm">
              <div className="h-40 bg-gradient-to-br from-[#1E4D40]/20 to-[#A3E635]/10 rounded-2xl flex items-center justify-center mb-4">
                <MapPin className="h-16 w-16 text-[#1E4D40]/40" />
              </div>
              <h3 className="font-semibold text-gray-900">Geographic Intelligence</h3>
              <p className="text-sm text-gray-500 mt-1">Heat maps of counterfeit hotspots</p>
            </div>
            <div className="bg-white rounded-3xl p-6 shadow-sm">
              <div className="h-40 bg-gradient-to-br from-[#A3E635]/10 to-[#1E4D40]/20 rounded-2xl flex items-center justify-center mb-4">
                <TrendingUp className="h-16 w-16 text-[#1E4D40]/40" />
              </div>
              <h3 className="font-semibold text-gray-900">AI Insights</h3>
              <p className="text-sm text-gray-500 mt-1">Data science powered predictions</p>
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 px-4">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-3xl font-bold text-gray-900 mb-4">Ready to protect your brand?</h2>
          <p className="text-lg text-gray-500 mb-8">Join the leading manufacturers in South Africa using VerifiAI.</p>
          <Link to="/register" className="inline-flex items-center px-8 py-4 text-base font-medium text-white bg-[#1E4D40] rounded-2xl hover:bg-[#163a30] shadow-lg">
            Get Started Free <ArrowRight className="h-5 w-5 ml-2" />
          </Link>
        </div>
      </section>

      <footer className="bg-[#1E4D40] text-white py-12 px-4">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between">
          <div className="flex items-center space-x-2 mb-4 md:mb-0">
            <Shield className="h-6 w-6 text-[#A3E635]" />
            <span className="font-bold">VerifiAI</span>
          </div>
          <p className="text-white/60 text-sm">&copy; 2025 VerifiAI. Intelligent Authentication for South Africa.</p>
        </div>
      </footer>
    </div>
  )
}

export default LandingPage
